import type { VisualArtEngine } from '../core/VisualArtEngine.js';
import type { ArtStateValues } from '../core/ArtState.js';
import type { VisualSignalBus } from '../signals/VisualSignalBus.js';
import type { VisualRecipe } from '../recipe/VisualRecipe.js';
import { ReplayPlayer } from '../replay/ReplayPlayer.js';
import type { ReplayData } from '../replay/ReplaySchema.js';
import type { AudioFeatureTimeline } from './AudioFeatures.js';
import { OfflineRenderClock } from './OfflineRenderClock.js';
import { OfflineSignalSource } from './OfflineSignalSource.js';
import {
  type AspectRatio,
  type FrameSize,
  type QualityPreset,
  type ResolutionName,
  aspectValue,
  fitToDevice,
  frameSize,
  qualityContext,
} from './RenderSettings.js';

/**
 * Re-makes an artwork from a performance, one frame at a time.
 *
 * This is the piece that turns Phase 4's claim into something usable. A replay
 * held a performance and no visuals; this regenerates the visuals from it at
 * whatever size, shape, frame rate and quality is asked for — and, because
 * nothing here reads a wall clock, twice with the same settings produces the
 * same film.
 *
 * It is kept out of `BridgeSession` deliberately. That class already composes a
 * live game; an offline renderer shares its engine and its bus and almost
 * nothing else, and folding one into the other would leave every field on it
 * ambiguous about which of the two it belonged to.
 *
 * The drawing surface arrives as a `RenderTarget`. That is the whole of this
 * file's knowledge of three — it resizes something, points a camera, draws, and
 * asks for bytes back.
 */

/** What the session needs from whatever is actually drawing. */
export interface RenderTarget {
  /** The largest dimension this device can render to. */
  readonly maxDimension: number;
  resize(size: FrameSize): void;
  /**
   * Points the camera for an aspect ratio.
   *
   * Not a resize. A 9:16 frame of a circular composition needs the camera pulled
   * back or the field rescaled, or the mandala is cropped at the sides — see
   * `CameraFraming`.
   */
  frame(framing: CameraFraming): void;
  /**
   * Draws the frame standing at `time`.
   *
   * The time is passed rather than read, for the same reason the clock is a
   * frame counter: everything the target animates — including whatever world
   * sits beneath the visual layers — must be a function of the frame index and
   * nothing else, or two renders of one performance differ in the background
   * while agreeing in every measured value.
   */
  render(time: number, deltaTime: number): void;
  /** PNG bytes for the current frame, or null if capture is unavailable. */
  capture(): Promise<Uint8Array | null>;
  /** A cheap fingerprint of what was drawn, for determinism checks. */
  hash(): Promise<string>;
}

export interface CameraFraming {
  readonly aspect: number;
  /**
   * How much of the field must stay visible, as a fraction of its radius.
   *
   * The composition is radial, so the binding constraint is the *short* edge.
   * A 16:9 frame is limited by its height and a 9:16 frame by its width, and
   * both must keep the whole mandala inside that edge.
   */
  readonly safeRadius: number;
  /** Multiplier on the world scale, from the quality preset's pixel ratio. */
  readonly scale: number;
}

export type ReRenderStatus = 'idle' | 'preparing' | 'rendering' | 'finished' | 'cancelled';

export interface ReRenderSettings {
  readonly resolution: ResolutionName;
  readonly aspect: AspectRatio;
  readonly fps: number;
  readonly quality: QualityPreset;
  /** Capture PNG bytes per frame. Off for a metrics-only or preview pass. */
  readonly capture?: boolean;
  /** Render only every Nth frame. Preview aid; 1 renders everything. */
  readonly frameStride?: number;
  /**
   * Cap on captured frames held in memory at once.
   *
   * A 4K PNG is several megabytes and a three-minute render is ten thousand
   * frames. Frames are handed to `onFrame` and released; if nobody takes them
   * the session stops capturing rather than filling the heap.
   */
  readonly maxBufferedFrames?: number;
}

export interface ReRenderInputs {
  readonly replay: ReplayData;
  readonly recipe: VisualRecipe;
  readonly beatTimes: readonly number[];
  readonly bpm: number;
  readonly firstBeatOffsetSeconds?: number;
  readonly features?: AudioFeatureTimeline | null;
}

export interface RenderedFrame {
  readonly index: number;
  readonly time: number;
  readonly bytes: Uint8Array;
}

export interface ReRenderProgress {
  readonly status: ReRenderStatus;
  readonly frame: number;
  readonly totalFrames: number;
  readonly progress: number;
  readonly songTime: number;
  readonly elapsedMs: number;
  readonly framesPerSecond: number;
  readonly estimatedRemainingFrames: number;
  readonly capturedFrames: number;
  readonly droppedCaptures: number;
}

export interface ReRenderOptions {
  readonly bus: VisualSignalBus;
  readonly engine: VisualArtEngine;
  readonly target: RenderTarget;
  /** Wall-clock reader, for progress only. Never used as a time source. */
  readonly now?: () => number;
  /**
   * Hands control back to whatever owns the event loop.
   *
   * Supplied rather than assumed, because this file is built without DOM or
   * Node types and has no `setTimeout` to reach for — which is the same rule
   * that keeps `three` out of it. A browser host passes a macrotask yield so a
   * cancel button can actually be clicked mid-render; a test passes a resolved
   * promise and the render runs as fast as the machine allows.
   */
  readonly yieldToHost?: () => Promise<void>;
}

export class ReRenderSession {
  private readonly bus: VisualSignalBus;
  private readonly engine: VisualArtEngine;
  private readonly target: RenderTarget;
  private readonly now: () => number;
  private readonly yieldToHost: () => Promise<void>;

  private clock = new OfflineRenderClock({ fps: 60, durationSeconds: 0 });
  private player: ReplayPlayer | null = null;
  private source: OfflineSignalSource | null = null;
  private settings: ReRenderSettings | null = null;

  private status: ReRenderStatus = 'idle';
  private cancelled = false;
  private startedAt = 0;
  private elapsed = 0;
  private captured = 0;
  private droppedCaptures = 0;
  private buffered = 0;
  private size: FrameSize = { width: 1920, height: 1080 };
  private downgradeReason: string | null = null;
  private hashes: { readonly frame: number; readonly hash: string }[] = [];

  constructor(options: ReRenderOptions) {
    this.bus = options.bus;
    this.engine = options.engine;
    this.target = options.target;
    this.now = options.now ?? (() => Date.now());
    this.yieldToHost = options.yieldToHost ?? (() => Promise.resolve());
  }

  get state(): ReRenderStatus {
    return this.status;
  }

  get frameSizeInUse(): FrameSize {
    return this.size;
  }

  get resolutionNote(): string | null {
    return this.downgradeReason;
  }

  /**
   * Configures a render without starting it.
   *
   * Everything that could fail — an impossible resolution, a replay with no
   * duration — is settled here, so `renderAll` either runs or was never a
   * plausible request.
   */
  prepare(inputs: ReRenderInputs, settings: ReRenderSettings): ReRenderProgress {
    this.status = 'preparing';
    this.cancelled = false;
    this.settings = settings;
    this.captured = 0;
    this.droppedCaptures = 0;
    this.buffered = 0;
    this.elapsed = 0;
    this.hashes = [];

    const requested = frameSize(settings.resolution, settings.aspect);
    const fitted = fitToDevice(requested, this.target.maxDimension);
    this.size = fitted.size;
    this.downgradeReason = fitted.reason;
    this.target.resize(this.size);

    const quality = qualityContext(settings.quality);
    this.target.frame({
      aspect: aspectValue(settings.aspect),
      // A radial composition is bounded by the short edge. Keeping the full
      // field inside it is what stops a 9:16 export from cropping the mandala.
      safeRadius: 1,
      scale: quality.pixelRatio,
    });

    // The song's full length, not the last event's: the piece keeps settling
    // through the trailing silence and a film should include it.
    const duration = Math.max(
      inputs.replay.song.durationSeconds,
      lastTimelineTime(inputs.replay),
    );
    this.clock = new OfflineRenderClock({ fps: settings.fps, durationSeconds: duration });

    this.bus.reset();
    this.engine.applyRecipe(inputs.recipe);
    // After the recipe, not before: quality is applied to the layers that exist,
    // and applying it first would set it on an empty stack and leave every
    // renderer built at LIVE however high the preset asked for.
    this.engine.setQuality(quality);
    this.engine.reset();

    this.source = new OfflineSignalSource({
      bus: this.bus,
      beatTimes: inputs.beatTimes,
      bpm: inputs.bpm,
      ...(inputs.firstBeatOffsetSeconds === undefined
        ? {}
        : { firstBeatOffsetSeconds: inputs.firstBeatOffsetSeconds }),
      features: inputs.features ?? null,
    });
    this.source.reset();

    this.player = new ReplayPlayer({ bus: this.bus });
    this.player.load(inputs.replay);
    this.player.play();

    return this.progress();
  }

  /**
   * Renders exactly one frame.
   *
   * The order matters and is the order §13 asks for: the clock defines the
   * moment, the performance and the song are replayed into that moment, the
   * engine reacts, and only then is anything drawn. Nothing in this method
   * reads a clock of its own.
   */
  async renderFrame(): Promise<RenderedFrame | null> {
    if (this.player === null || this.source === null || this.settings === null) return null;
    const startedAt = this.now();
    if (this.startedAt === 0) this.startedAt = startedAt;

    const time = this.clock.currentTime;
    const previous = Math.max(0, this.clock.previousTime);

    // 1–4: the song's signals for this frame's span, every beat inside it.
    this.source.sampleFrame(previous, time);

    // 5–6: the performance, up to this instant. `seek` with the player already
    // past the point is a no-op, so this only ever moves forward.
    this.player.seek(time);

    // 7–8: mutations, art state and renderer updates, at a fixed delta.
    this.engine.update(time, this.clock.deltaTime);

    // 9: draw, at this frame's moment and no other.
    this.engine.render();
    this.target.render(time, this.clock.deltaTime);

    // 10: capture.
    let frame: RenderedFrame | null = null;
    if (this.settings.capture === true) {
      const limit = this.settings.maxBufferedFrames ?? 8;
      if (this.buffered >= limit) {
        this.droppedCaptures += 1;
      } else {
        const bytes = await this.target.capture();
        if (bytes !== null) {
          this.buffered += 1;
          this.captured += 1;
          frame = { index: this.clock.frameIndex, time, bytes };
        }
      }
    }

    this.elapsed = this.now() - this.startedAt;
    return frame;
  }

  /** Tells the session a captured frame has been written and may be released. */
  releaseFrame(): void {
    if (this.buffered > 0) this.buffered -= 1;
  }

  /** Moves to the next frame. False when the last one has been rendered. */
  advance(): boolean {
    const stride = Math.max(1, this.settings?.frameStride ?? 1);
    let moved = false;
    for (let step = 0; step < stride; step += 1) {
      if (!this.clock.advance()) break;
      moved = true;
    }
    if (!moved) this.status = 'finished';
    return moved;
  }

  /**
   * Renders every frame, yielding between them.
   *
   * `onFrame` is awaited, which is what keeps memory bounded: the caller writes
   * or encodes the frame and only then does the next one get made. Yielding also
   * lets a cancel arrive — without it the whole render is one uninterruptible
   * task and the button does nothing.
   */
  async renderAll(handlers: {
    readonly onFrame?: (frame: RenderedFrame) => void | Promise<void>;
    readonly onProgress?: (progress: ReRenderProgress) => void;
    /** Frames to fingerprint. Defaults to first, quarters and last. */
    readonly hashFrames?: readonly number[];
    readonly yieldEvery?: number;
  } = {}): Promise<ReRenderProgress> {
    if (this.player === null) return this.progress();
    this.status = 'rendering';
    this.startedAt = this.now();

    const total = this.clock.totalFrames;
    const wanted = new Set(
      handlers.hashFrames ??
        [0, Math.floor((total - 1) * 0.25), Math.floor((total - 1) * 0.5),
         Math.floor((total - 1) * 0.75), total - 1],
    );
    const yieldEvery = Math.max(1, handlers.yieldEvery ?? 8);

    for (;;) {
      if (this.cancelled) {
        this.status = 'cancelled';
        return this.progress();
      }

      const frame = await this.renderFrame();
      if (wanted.has(this.clock.frameIndex)) {
        this.hashes.push({ frame: this.clock.frameIndex, hash: await this.target.hash() });
      }
      if (frame !== null && handlers.onFrame !== undefined) {
        await handlers.onFrame(frame);
        this.releaseFrame();
      } else if (frame !== null) {
        // Nobody took it, so it must not be counted against the budget.
        this.releaseFrame();
      }

      if (this.clock.frameIndex % yieldEvery === 0) {
        handlers.onProgress?.(this.progress());
        // Whatever the host considers a yield. In a browser this must be a
        // macrotask, or a cancel click never gets a chance to run.
        await this.yieldToHost();
      }

      if (!this.advance()) break;
    }

    this.status = this.cancelled ? 'cancelled' : 'finished';
    handlers.onProgress?.(this.progress());
    return this.progress();
  }

  /** Renders one specific moment, for a still. */
  async renderStillAt(seconds: number): Promise<RenderedFrame | null> {
    if (this.player === null) return null;
    // From the beginning: the artwork accumulates, so a still at 90 seconds is
    // only correct if the first 90 seconds actually happened.
    this.restart();
    this.clock.seekToTime(seconds);
    const target = this.clock.frameIndex;
    this.clock.reset();

    const requested = this.settings;
    // The run-up is stepped but never captured. Capturing it would fill the
    // frame budget with images nobody asked for and leave none for the still —
    // which is exactly what happened the first time this was written.
    this.settings = requested === null ? null : { ...requested, capture: false };
    for (let index = 0; index < target; index += 1) {
      await this.renderFrame();
      this.clock.advance();
    }

    this.settings = requested === null ? null : { ...requested, capture: true };
    const frame = await this.renderFrame();
    this.settings = requested;
    if (frame !== null) this.releaseFrame();
    return frame;
  }

  private restart(): void {
    this.clock.reset();
    this.bus.reset();
    this.engine.reset();
    this.source?.reset();
    this.player?.restart();
    this.player?.play();
  }

  cancel(): void {
    this.cancelled = true;
    this.status = 'cancelled';
  }

  /** Releases everything the render held. The engine and bus are not ours. */
  dispose(): void {
    this.player?.stop();
    this.player = null;
    this.source = null;
    this.settings = null;
    this.buffered = 0;
    this.status = 'idle';
  }

  progress(): ReRenderProgress {
    const total = this.clock.totalFrames;
    const frame = this.clock.frameIndex;
    const seconds = this.elapsed / 1000;
    return {
      status: this.status,
      frame,
      totalFrames: total,
      progress: this.clock.progress,
      songTime: this.clock.currentTime,
      elapsedMs: Math.round(this.elapsed),
      framesPerSecond: seconds > 0 ? Number((frame / seconds).toFixed(2)) : 0,
      estimatedRemainingFrames: Math.max(0, total - 1 - frame),
      capturedFrames: this.captured,
      droppedCaptures: this.droppedCaptures,
    };
  }

  /**
   * Everything a determinism check compares.
   *
   * Deliberately not the frames themselves. Two renders that agree on the art
   * state, the mutation count and a handful of frame fingerprints agree on
   * everything that matters, and comparing ten thousand PNGs to prove it would
   * cost more than the render did.
   */
  /**
   * A fingerprint of the frame that was last drawn.
   *
   * Exposed so a video export can fingerprint what it is about to encode. The
   * comparison has to happen on the *picture*, before the encoder touches it:
   * two runs of one performance produce identical frames but not identical
   * compressed bytes, because an encoder is free to spend its bitrate
   * differently on an identical input.
   */
  async fingerprintFrame(): Promise<string> {
    return this.target.hash();
  }

  fingerprint(): {
    readonly artState: ArtStateValues;
    readonly mutations: number;
    readonly frames: number;
    readonly fps: number;
    readonly size: FrameSize;
    readonly signals: ReturnType<VisualSignalBus['diagnostics']>;
    readonly source: ReturnType<OfflineSignalSource['diagnostics']> | null;
    readonly hashes: readonly { readonly frame: number; readonly hash: string }[];
  } {
    return {
      artState: this.engine.artState,
      mutations: this.engine.diagnostics().mutationsDispatched,
      frames: this.clock.totalFrames,
      fps: this.clock.fps,
      size: this.size,
      signals: this.bus.diagnostics(),
      source: this.source?.diagnostics() ?? null,
      hashes: [...this.hashes],
    };
  }
}

function lastTimelineTime(replay: ReplayData): number {
  const lastInput = replay.timeline.playerInputs.at(-1)?.time ?? 0;
  const lastEvent = replay.timeline.rhythmEvents.at(-1)?.time ?? 0;
  return Math.max(lastInput, lastEvent);
}
